import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { COOKIE_CONSENT_CATEGORIES } from '../cookie/cookie-consent.consts';
import { CookieConsentService } from '../cookie/cookie-consent.service';
import { StorageFeature } from './storage.types';

/**
 * Service used to handle localstorage data storing.
 * Internally, the service uses the cookie consents to enable or disable storing of data.
 * Each 'feature' is linked to a cookie category by a map.
 * Add an entry to the object for every kind of data which needs to be stored.
 */
@Injectable({
    providedIn: 'root',
})
export class StorageService {
    private readonly cookieConsentService = inject(CookieConsentService);

    /**
     * Maps a storage feature to its cookie consent category.
     * Used to verify, before writing any data, that we have consent to do so.
     * @private
     */
    private storageCookieCategoryMap = new Map<string, string>([['language', COOKIE_CONSENT_CATEGORIES.PERFORMANCE]]);

    constructor() {
        this.cookieConsentService.consentChange$.pipe(takeUntilDestroyed()).subscribe((lostCategory: string) => {
            this.deleteCategoryData(lostCategory);
        });
    }

    /**
     * Returns true if the data was written, false otherwise.
     * Failure to store data would depend on the cookie consent.
     * Any new key feature must be added
     */
    storeData(key: StorageFeature, value: string | null): boolean {
        //We need to verify the permission for the appropriate cookie consent category based on the key.
        const cookieCategory = this.storageCookieCategoryMap.get(key);

        if (!cookieCategory) {
            console.warn('Storage feature missing in cookie category map');
            return false;
        } else {
            //Check cookie category, return false and prevent storing if not accepted
            if (!this.cookieConsentService?.isCategoryAccepted(cookieCategory)) return false;
        }

        localStorage.setItem(key, value);

        return true;
    }

    /**
     * Deletes all data related to a Cookie Consent category
     */
    deleteCategoryData(category: string) {
        this.storageCookieCategoryMap.forEach((mappedCategory, key) => {
            if (mappedCategory === category) {
                localStorage.removeItem(key);
            }
        });
    }

    getData(key: StorageFeature): string | null {
        return localStorage.getItem(key);
    }
}
